import { PAYLOAD_URL } from "~/constants/all";
import { getNativeToken } from "~/lib/nativeToken";
import type { FetchOptions, RequestBody } from "~/types/apiAndHookBase";

// Shared refresh promise to prevent concurrent token refresh calls.
// When multiple requests get 401 simultaneously, they all share a single refresh.
let refreshPromise: Promise<Response> | null = null;

function getAuthHeaders(): Record<string, string> {
  const token = getNativeToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Makes a file REST API request
 * @param url - The URL of the API endpoint
 * @param formData - The form data to be sent
 * @param options - Additional fetch options
 * @param retry - Flag to retry the request if unauthorized (default: true)
 * @returns A promise that resolves to the response data
 */
export const fileRest = async <T = unknown>(
  url: string,
  formData: FormData,
  options: FetchOptions = {},
  retry = true
): Promise<T> => {
  const method = options?.method || "POST";
  const res = await fetch(url, {
    method,
    ...(method === "POST" || method === "PATCH" ? { body: formData } : {}),
    credentials: "include",
    headers: {
      ...getAuthHeaders(),
      ...options?.headers,
    },
    ...options,
  });

  // If the request is unauthorized, try to refresh the token and retry the request.
  if (res.status === 401 && retry) {
    if (!refreshPromise) {
      refreshPromise = fetch(`${PAYLOAD_URL}/api/users/refresh-token`, {
        method: "POST",
        credentials: "include",
        headers: { ...getAuthHeaders() },
      }).finally(() => {
        refreshPromise = null;
      });
    }
    const refreshResponse = await refreshPromise;
    if (!refreshResponse.ok) {
      throw new Error("Failed to refresh token");
    }
    // Retry the original request.
    return fileRest(url, formData, options, false);
  }

  const data = (await res.json()) as T;
  return data;
};

/**
 * Makes a public REST API request
 * @param url - The URL of the API endpoint
 * @param args - The arguments to be sent in the request body
 * @param options - Additional fetch options
 * @param retry - Flag to retry the request if unauthorized (default: true)
 * @returns A promise that resolves to the response data
 */
export const restPublic = async <T = unknown>(
  url: string,
  args: RequestBody = {},
  options: FetchOptions = {}
): Promise<T> => {
  const method = options?.method || "POST";
  const res = await fetch(url, {
    method,
    ...(method === "POST" || method === "PATCH"
      ? { body: JSON.stringify(args) }
      : {}),
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    ...options,
  });

  const data = (await res.json()) as T;
  return data;
};

/**
 * Makes a public REST API request for PDF
 * @param url - The URL of the API endpoint
 * @param args - The arguments to be sent in the request body
 * @param options - Additional fetch options
 * @param retry - Flag to retry the request if unauthorized (default: true)
 * @returns A promise that resolves to the response data
 */
export const restPublicPdf = async (
  url: string,
  args: RequestBody = {},
  options: FetchOptions = {}
): Promise<string> => {
  const method = options?.method || "POST";
  const res = await fetch(url, {
    method,
    ...(method === "POST" || method === "PATCH"
      ? { body: JSON.stringify(args) }
      : {}),
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    ...options,
  });

  const buffer = await res.arrayBuffer();
  const int8 = new Uint8Array(buffer);
  const data = Buffer.from(int8).toString("base64");

  return data;
};

/**
 * Makes a REST API request
 * @param url - The URL of the API endpoint
 * @param args - The arguments to be sent in the request body
 * @param options - Additional fetch options
 * @param retry - Flag to retry the request if unauthorized (default: true)
 * @returns A promise that resolves to the response data
 */
const rest = async <T = unknown>(
  url: string,
  args: RequestBody = {},
  options: FetchOptions = {},
  retry = true,
  hasJsonResponse = true
): Promise<T> => {
  const method = options?.method || "POST";
  const res = await fetch(url, {
    method,
    ...(method === "POST" || method === "PATCH"
      ? { body: JSON.stringify(args) }
      : {}),
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...options?.headers,
    },
    ...options,
  });

  // If the request is unauthorized, try to refresh the token and retry the request.
  if (res.status === 401 && retry) {
    if (!refreshPromise) {
      refreshPromise = fetch(`${PAYLOAD_URL}/api/users/refresh-token`, {
        method: "POST",
        credentials: "include",
        headers: { ...getAuthHeaders() },
      }).finally(() => {
        refreshPromise = null;
      });
    }
    const refreshResponse = await refreshPromise;
    if (!refreshResponse.ok) {
      throw new Error("Failed to refresh token");
    }
    // Retry the original request.
    return rest(url, args, options, false);
  }

  if (!hasJsonResponse) {
    return undefined as T;
  }

  const data = (await res.json()) as T;
  return data;
};

export default rest;
